import { createFileRoute, Link } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { useSubmissions, useTeachers } from "@/projects/lib/useStore";
import {
  FileVideo, Clock, UserCheck, AlertTriangle, Trophy, Users,
  TrendingUp, TrendingDown, Activity, CalendarDays, ArrowRight,
} from "lucide-react";
import { cn } from "@/projects/lib/utils";
import { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area,
  PieChart, Pie, Cell, CartesianGrid, Legend, LineChart, Line,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import { StatusBadge } from "@/projects/components/status-badge";
import { AiInsightsPanel } from "@/projects/components/ai-insights-panel";

export const Route = createFileRoute("/admin/")({
  head: () => ({ meta: [{ title: "Admin Dashboard — રાજનગર જ્ઞાનોત્સવ 2.0" }] }),
  component: AdminDashboard,
});

const C = {
  royal: "oklch(0.42 0.15 265)",
  royalLight: "oklch(0.62 0.16 275)",
  gold: "oklch(0.78 0.14 85)",
  saffron: "oklch(0.72 0.18 55)",
  success: "oklch(0.62 0.16 145)",
  danger: "oklch(0.58 0.22 27)",
  info: "oklch(0.55 0.15 240)",
  muted: "oklch(0.85 0.02 265)",
};

type Range = "7d" | "30d" | "all";

function AdminDashboard() {
  const submissions = useSubmissions();
  const teachers = useTeachers();
  const [range, setRange] = useState<Range>("30d");

  const filtered = useMemo(() => {
    if (range === "all") return submissions;
    const days = range === "7d" ? 7 : 30;
    const cut = Date.now() - days * 24 * 3600 * 1000;
    return submissions.filter((s) => new Date(s.submittedAt).getTime() >= cut);
  }, [submissions, range]);

  const counts = {
    total: filtered.length,
    pending: filtered.filter((s) => s.status === "Pending").length,
    assigned: filtered.filter((s) => s.status === "Assigned for Verification").length,
    notProper: filtered.filter((s) => s.status === "Not Proper").length,
    reward: filtered.filter((s) => s.status === "Ready for Reward").length,
    teachers: teachers.length,
  };

  const verificationRate = counts.total
    ? Math.round(((counts.reward + counts.notProper) / counts.total) * 100)
    : 0;
  const rewardRate = counts.total ? Math.round((counts.reward / counts.total) * 100) : 0;

  // Daily trend
  const byDate = useMemo(() => {
    const map = new Map<string, { subs: number; verified: number }>();
    const days = range === "7d" ? 7 : range === "30d" ? 30 : 60;
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000);
      const k = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
      map.set(k, { subs: 0, verified: 0 });
    }
    filtered.forEach((s) => {
      const k = new Date(s.submittedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
      const cur = map.get(k) ?? { subs: 0, verified: 0 };
      cur.subs += 1;
      if (s.status === "Ready for Reward" || s.status === "Not Proper") cur.verified += 1;
      map.set(k, cur);
    });
    return Array.from(map.entries()).map(([date, v]) => ({ date, ...v }));
  }, [filtered, range]);

  const spark = (key: "subs" | "verified") => byDate.map((d) => ({ v: d[key] }));

  const byStatus = [
    { name: "Pending", value: counts.pending, color: C.saffron },
    { name: "Assigned", value: counts.assigned, color: C.royal },
    { name: "Not Proper", value: counts.notProper, color: C.danger },
    { name: "Ready for Reward", value: counts.reward, color: C.success },
  ];

  const byTeacher = teachers.map((t) => ({
    name: t.name.split(" ")[0],
    reward: filtered.filter((s) => s.assignedTeacherId === t.id && s.status === "Ready for Reward").length,
    notProper: filtered.filter((s) => s.assignedTeacherId === t.id && s.status === "Not Proper").length,
    pending: filtered.filter((s) => s.assignedTeacherId === t.id && s.status === "Assigned for Verification").length,
  })).sort((a, b) => (b.reward + b.notProper) - (a.reward + a.notProper));

  const topTeachers = byTeacher.slice(0, 5).map((t) => ({ name: t.name, verified: t.reward + t.notProper }));

  const gauge = [{ name: "rate", value: rewardRate, fill: C.gold }];

  const recent = [...filtered]
    .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
    .slice(0, 6);

  const kpis = [
    { label: "Total Submissions", value: counts.total, delta: 12, up: true, icon: FileVideo, tone: "royal", spark: spark("subs") },
    { label: "Pending Review", value: counts.pending, delta: 4, up: false, icon: Clock, tone: "saffron", spark: spark("subs") },
    { label: "Assigned", value: counts.assigned, delta: 8, up: true, icon: UserCheck, tone: "info", spark: spark("verified") },
    { label: "Ready for Reward", value: counts.reward, delta: 18, up: true, icon: Trophy, tone: "success", spark: spark("verified") },
    { label: "Not Proper", value: counts.notProper, delta: 2, up: false, icon: AlertTriangle, tone: "danger", spark: spark("verified") },
    { label: "Active Teachers", value: counts.teachers, delta: 0, up: true, icon: Users, tone: "gold", spark: spark("subs") },
  ];

  return (
    <DashboardLayout role="admin" title="Executive Dashboard" breadcrumb={["Home", "Dashboard"]}>
      {/* Filter ribbon */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border bg-gradient-to-r from-royal-soft via-card to-gold-soft/40 p-3 shadow-soft">
        <div className="flex items-center gap-2 text-sm font-semibold text-royal">
          <Activity className="h-4 w-4" /> Live Insights
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1 rounded-lg border bg-background p-1 text-xs">
          <CalendarDays className="ml-2 h-3.5 w-3.5 text-muted-foreground" />
          {(["7d", "30d", "all"] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={cn(
                "rounded-md px-3 py-1.5 font-medium transition-colors",
                range === r ? "bg-royal text-white shadow-soft" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {r === "7d" ? "Last 7 days" : r === "30d" ? "Last 30 days" : "All time"}
            </button>
          ))}
        </div>
      </div>

      {/* KPI tiles with sparklines */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {kpis.map((k) => (
          <Link
            key={k.label}
            to={k.label === "Active Teachers" ? "/admin/teachers" : "/admin/submissions"}
            className="group relative overflow-hidden rounded-xl border bg-card p-4 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-card"
          >
            <div className={cn(
              "absolute inset-x-0 top-0 h-1",
              k.tone === "royal" && "bg-royal",
              k.tone === "saffron" && "bg-saffron",
              k.tone === "gold" && "bg-gold",
              k.tone === "success" && "bg-success",
              k.tone === "danger" && "bg-destructive",
              k.tone === "info" && "bg-info",
            )} />
            <div className="flex items-start justify-between">
              <div className={cn(
                "grid h-9 w-9 place-items-center rounded-lg",
                k.tone === "royal" && "bg-royal-soft text-royal",
                k.tone === "saffron" && "bg-saffron-soft text-saffron",
                k.tone === "gold" && "bg-gold-soft text-gold",
                k.tone === "success" && "bg-success/10 text-success",
                k.tone === "danger" && "bg-destructive/10 text-destructive",
                k.tone === "info" && "bg-royal-soft text-info",
              )}>
                <k.icon className="h-4.5 w-4.5" />
              </div>
              <div className={cn(
                "flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                k.up ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
              )}>
                {k.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {k.delta}%
              </div>
            </div>
            <div className="mt-3 text-2xl font-bold tracking-tight">{k.value}</div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{k.label}</div>
            <div className="mt-2 h-8">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={k.spark}>
                  <defs>
                    <linearGradient id={`sp-${k.label}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.royal} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={C.royal} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke={C.royal} strokeWidth={1.5} fill={`url(#sp-${k.label})`} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Link>
        ))}
      </div>

      {/* AI Insights */}
      <div className="mt-4">
        <AiInsightsPanel
          range={range === "7d" ? "Last 7 days" : range === "30d" ? "Last 30 days" : "All time"}
          total={counts.total}
          pending={counts.pending}
          assigned={counts.assigned}
          reward={counts.reward}
          notProper={counts.notProper}
          teachers={counts.teachers}
          rewardRate={rewardRate}
          verificationRate={verificationRate}
          topTeachers={topTeachers}
        />
      </div>

      {/* Main chart grid */}
      <div className="mt-4 grid gap-4 lg:grid-cols-6">

        {/* Big area chart */}
        <Panel title="Submission Trend" subtitle="Daily submissions vs verifications" className="lg:col-span-4">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={byDate} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="g-subs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.royal} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={C.royal} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="g-ver" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.gold} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={C.gold} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="date" fontSize={10} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid oklch(0.92 0.02 260)" }} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
              <Area type="monotone" dataKey="subs" name="Submissions" stroke={C.royal} strokeWidth={2} fill="url(#g-subs)" />
              <Area type="monotone" dataKey="verified" name="Verified" stroke={C.gold} strokeWidth={2} fill="url(#g-ver)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        {/* Reward rate gauge */}
        <Panel title="Reward Rate" subtitle="Ready for Reward %" className="lg:col-span-2">
          <div className="relative">
            <ResponsiveContainer width="100%" height={230}>
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={gauge} startAngle={220} endAngle={-40}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={12} background={{ fill: "oklch(0.94 0.02 265)" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-4xl font-extrabold text-royal">{rewardRate}%</div>
              <div className="text-xs text-muted-foreground">of {counts.total} submissions</div>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs">
            <span className="text-muted-foreground">Verification rate</span>
            <span className="font-semibold text-royal">{verificationRate}%</span>
          </div>
        </Panel>

        {/* Status donut */}
        <Panel title="Status Distribution" subtitle="Breakdown across pipeline" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={byStatus} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3} stroke="none">
                {byStatus.map((s, i) => <Cell key={i} fill={s.color} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {byStatus.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                <span className="truncate text-muted-foreground">{s.name}</span>
                <span className="ml-auto font-semibold">{s.value}</span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Teacher performance stacked bar */}
        <Panel title="Teacher Performance" subtitle="Verification workload split by outcome" className="lg:col-span-4">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byTeacher} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="name" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              <Bar dataKey="reward" name="Ready for Reward" stackId="a" fill={C.success} radius={[0,0,0,0]} />
              <Bar dataKey="notProper" name="Not Proper" stackId="a" fill={C.danger} />
              <Bar dataKey="pending" name="Pending" stackId="a" fill={C.royal} radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        {/* Top performers */}
        <Panel title="Top Verifiers" subtitle="By total processed" className="lg:col-span-3">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topTeachers} layout="vertical" margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
              <XAxis type="number" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" fontSize={11} tickLine={false} axisLine={false} width={70} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="verified" fill={C.royal} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        {/* Weekly submission mini line */}
        <Panel title="Momentum" subtitle="7-day rolling velocity" className="lg:col-span-3">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={byDate.slice(-14)} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="date" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="subs" name="Submissions" stroke={C.royal} strokeWidth={2.5} dot={{ r: 3, fill: C.royal }} />
              <Line type="monotone" dataKey="verified" name="Verified" stroke={C.gold} strokeWidth={2.5} dot={{ r: 3, fill: C.gold }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Recent activity feed */}
      <Panel title="Recent Submissions" subtitle="Latest activity across the platform" className="mt-4">
        {recent.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No submissions in range.</div>
        ) : (
          <div className="divide-y">
            {recent.map((s) => (
              <div key={s.id} className="flex items-center gap-3 py-2.5">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-royal-soft text-royal">
                  <FileVideo className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{s.videoTitle}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.studentName} · <span className="font-mono">{s.id}</span> · {new Date(s.submittedAt).toLocaleString()}
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex justify-end">
          <Link to="/admin/submissions" className="inline-flex items-center gap-1 text-xs font-medium text-royal hover:underline">
            View all submissions <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </Panel>
    </DashboardLayout>
  );
}

function Panel({
  title, subtitle, children, className,
}: { title: string; subtitle?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border bg-card p-4 shadow-soft sm:p-5", className)}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}
